import { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "antd";
import { PlusOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AuthContext from "../../context/AuthContext";
import { useProjects } from "../../queries/projects";
import { useTimeEntries } from "../../queries/timeEntries";
import { useTickets } from "../../queries/tickets";
import { useTasks } from "../../queries/tasks";
import { PageShell } from "../../components/ui/PageShell";
import { TaskStatusTag } from "../../components/ui/StatusTag";
import { EmptyState, CardSkeleton } from "../../components/ui/States";
import { NewTicketModal } from "../../components/pages/tickets/NewTicketModal";
import { ManagerDashboard } from "../../components/pages/dashboard/ManagerDashboard";
import { SeverityTag } from "../../components/pages/dashboard/Tags";
import { routes } from "../../navigation/routes/routes";
import { ProjectProps, TaskProps, TicketProps, TimeEntryProps } from "../../types/interfaces";
import styles from "./dashboard.module.css";

const NO_PROJECTS: ProjectProps[] = [];
const NO_TASKS: TaskProps[] = [];
const OPEN: TaskProps["status"][] = ["ready", "in_progress", "in_review"];

const Stat = ({ label, value, note, alert }: { label: string; value: string | number; note?: string; alert?: boolean }) => (
  <div className={styles.stat}>
    <div className="osi-kicker">{label}</div>
    <div className={`${styles.statValue} ${alert ? styles.statAlert : ""}`}>{value}</div>
    {note && <div className={styles.statNote}>{note}</div>}
  </div>
);

/**
 * Home. Managers get the full reporting dashboard; agents get "my work";
 * requesters get a simple view of their own tickets.
 */
const MyWorkDashboard = ({ isRequester }: { isRequester: boolean }) => {
  const navigate = useNavigate();
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const { userData } = useContext(AuthContext);
  const myId = userData?._id;

  const { data: projects } = useProjects();
  const { data: myTasks, isLoading } = useTasks({ assigneeId: myId, enabled: !isRequester && !!myId });
    const projectName = useMemo(() => {
    const list: ProjectProps[] = projects ?? NO_PROJECTS;
    return new Map(list.map((p) => [p._id, p.name] as [string, string]));
  }, [projects]);

  const weekStart = dayjs().startOf("week");
  const { data: entries } = useTimeEntries({
    userId: myId,
    from: weekStart.format("YYYY-MM-DD"),
    to: dayjs().endOf("week").format("YYYY-MM-DD"),
  }, { enabled: !isRequester });
  const { data: tickets, isLoading: ticketsLoading } = useTickets(
    isRequester ? { solved: false } : { solved: false, assigneeId: myId }
  );

  const ticketList: TicketProps[] = tickets || [];
  const mine = useMemo(
    () => {
      const list: TaskProps[] = myTasks ?? NO_TASKS;
      return list.filter((t) => OPEN.includes(t.status));
    },
    [myTasks]
  );
  const overdue = useMemo(
    () => mine.filter((t) => t.dueDate && dayjs(t.dueDate).isBefore(dayjs(), "day")),
    [mine]
  );

  const byDay = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));
    // `entries` is `TimeEntryProps[] | undefined`; annotating the fallback
    // keeps it a single concrete array type rather than a
    // `TimeEntryProps[] | never[]` union, which TS can't infer filter/reduce
    // callback parameter types across (surfaces as implicit-any under strict).
    const entryList: TimeEntryProps[] = entries || [];
    return days.map((d) => ({
      day: d,
      minutes: entryList
        .filter((e) => dayjs(e.date).isSame(d, "day"))
        .reduce((n, e) => n + e.durationMinutes, 0),
    }));
  }, [entries, weekStart]);

  const weekMinutes = byDay.reduce((n, d) => n + d.minutes, 0);
  const peak = Math.max(...byDay.map((d) => d.minutes), 60);
  const hours = (m: number) => (m / 60).toFixed(1);

  const greeting = dayjs().hour() < 12 ? "Good morning" : dayjs().hour() < 18 ? "Good afternoon" : "Good evening";

  return (
    <PageShell
      kind="dashboard"
      kicker={dayjs().format("dddd, D MMMM")}
      title={`${greeting}, ${(userData?.name || "").split(" ")[0]}`}
      subtitle={isRequester ? "Your open support tickets and their status." : undefined}
      actions={
        <>
          {!isRequester && <Button icon={<ClockCircleOutlined />} onClick={() => navigate(routes.myTime)}>Log time</Button>}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewTicketOpen(true)}>
            New ticket
          </Button>
        </>
      }
    >
      <div className={styles.stats}>
        {!isRequester && <Stat label="My open tasks" value={mine.length} note={`across ${new Set(mine.map((t) => t.projectId)).size} projects`} />}
        {!isRequester && <Stat label="Overdue" value={overdue.length} alert={overdue.length > 0} note={overdue.length ? "needs attention today" : "nothing late"} />}
        {!isRequester && <Stat label="Logged this week" value={`${hours(weekMinutes)}h`} note={`${hours(weekMinutes / Math.max(dayjs().day(), 1))}h/day average`} />}
        <Stat label={isRequester ? "Open tickets" : "My open tickets"} value={ticketList.length} note={isRequester ? "not yet solved" : "assigned to you"} />
      </div>

      <div className={styles.grid}>
        {!isRequester && <section className={styles.panel}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>My open tasks</span>
            <Button type="link" size="small" className={styles.panelLink} onClick={() => navigate(routes.projects)}>
              All projects →
            </Button>
          </div>
          {isLoading ? (
            <CardSkeleton height={180} />
          ) : mine.length === 0 ? (
            <EmptyState
              title="Nothing assigned to you"
              description="When someone moves a task to Ready and puts your name on it, it shows up here."
            />
          ) : (
            mine.slice(0, 8).map((t) => (
              <Link key={t._id} className={styles.taskRow} to={routes.taskDetail.replace(":id", t._id)}>
                <TaskStatusTag status={t.status} />
                <span className={styles.taskTitle}>{t.title}</span>
                <span className={styles.taskProject}>{projectName.get(t.projectId)}</span>
              </Link>
            ))
          )}
        </section>}

        {isRequester && <section className={styles.panel}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>My open tickets</span>
            <Button type="link" size="small" className={styles.panelLink} onClick={() => navigate(routes.tickets)}>
              All tickets →
            </Button>
          </div>
          {ticketsLoading ? (
            <CardSkeleton height={180} />
          ) : ticketList.length === 0 ? (
            <EmptyState title="No open tickets" description="Raise a ticket and it will show up here with its status." />
          ) : (
            ticketList.slice(0, 8).map((t) => (
              <Link key={t._id} className={styles.taskRow} to={routes.ticketDetail.replace(":id", t._id)}>
                <SeverityTag severity={t.severity} />
                <span className={styles.taskTitle}>{t.subject}</span>
                <span className={styles.taskProject}>{t.firstResponseAt ? "Answered" : "Awaiting reply"}</span>
              </Link>
            ))
          )}
        </section>}

        <aside className={styles.panel}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>{isRequester ? "Support" : "This week"}</span>
            <span className={styles.panelLink + " osi-num"} style={{ color: "var(--osi-text-faint)" }}>
              {!isRequester && `${hours(weekMinutes)}h`}
            </span>
          </div>
          {!isRequester && <div className={styles.week}>
            {byDay.map(({ day, minutes }) => (
              <div className={styles.day} key={day.toString()}>
                <div
                  className={`${styles.bar} ${day.isSame(dayjs(), "day") ? styles.barToday : ""}`}
                  style={{ height: `${Math.max((minutes / peak) * 100, 2)}%` }}
                  title={`${hours(minutes)}h`}
                />
                <div className={styles.dayLabel}>{day.format("dd")[0]}</div>
              </div>
            ))}
          </div>}
          <div className={styles.quick}>
            {!isRequester && <Button block onClick={() => navigate(routes.myTime)}>Open timesheet</Button>}
            <Button block onClick={() => navigate(routes.tickets)}>Support queue</Button>
          </div>
        </aside>
      </div>
      <NewTicketModal open={newTicketOpen} onClose={() => setNewTicketOpen(false)} />
    </PageShell>
  );
};

export const DashboardPage = () => {
  const { userData } = useContext(AuthContext);
  if (userData?.role === "manager") return <ManagerDashboard />;
  return <MyWorkDashboard isRequester={userData?.role === "requester"} />;
};
